'use client';

import { useState, useEffect, useCallback } from 'react';
import { useSession } from 'next-auth/react';
import Link from 'next/link';
import Image from 'next/image';
import {
  Wallet,
  TrendingUp,
  BarChart3,
  DollarSign,
  Plus,
  Loader2,
  Gamepad2,
  Trophy,
  TrendingDown,
  RefreshCw,
  Crown,
} from 'lucide-react';
import GameBuySell from '@/components/GameBuySell';
import toast from 'react-hot-toast';

type Account = {
  seasonId: string;
  label: string;
  accountId: string;
  cashBalance: number;
  positionsValue: number;
  positionsMarketValue?: number;
  totalValue: number;
  realizedPl?: number;
  unrealizedPl?: number;
};

type Position = {
  id: string;
  symbol: string;
  shares: number;
  avgCost: number;
  bookValue: number;
  currentPrice?: number | null;
  marketValue?: number;
  unrealizedPl?: number;
  unrealizedPlPct?: number;
  stopPrice?: number | null;
  manualStop?: boolean;
};

type Snapshot = {
  timestamp: string;
  totalValue: number;
  cashBalance: number;
  positionsValue: number;
};

type GameRanking = {
  rank: number;
  userId: string;
  username: string;
  avatar: string | null;
  value: number;
  realizedPl?: number;
  unrealizedPl?: number;
  /** Trade-level: symbol and asterisk if unrealized (biggest winner/loser) */
  symbol?: string;
  isUnrealized?: boolean;
};

function formatMoney(n: number) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n);
}

function formatShares(n: number) {
  return n.toFixed(4);
}

function formatRelTime(ts: string): string {
  const diff = Date.now() - new Date(ts).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

const THOTH_AVATAR   = '/assets/thoth/thoth-avatar.png';
const CLOUD_AVATAR   = '/assets/cloud/cloud-avatar.png';
const RAGNAR_AVATAR  = '/assets/ragnar/ragnar-avatar.png';
const RA_AVATAR      = '/assets/ra/ra-avatar.png';
const OSIRIS_AVATAR  = '/assets/osiris/osiris-avatar.png';
const SOLOMON_AVATAR = '/assets/solomon/solomon-avatar.webp';
const WATCHER_AVATAR = '/assets/watcher/watcher-avatar.png';

export default function GamePage() {
  const { data: session, status } = useSession();
  const [account, setAccount] = useState<Account | null>(null);
  const [positions, setPositions] = useState<Position[]>([]);
  const [snapshots, setSnapshots] = useState<Snapshot[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [buyNewSymbol, setBuyNewSymbol] = useState<string | null>(null);
  const [rankingsMetric, setRankingsMetric] = useState<'account_value' | 'biggest_winner' | 'biggest_loser'>('account_value');
  const [rankings, setRankings] = useState<GameRanking[]>([]);
  const [rankingsLoading, setRankingsLoading] = useState(false);
  const [rankingsError, setRankingsError] = useState<string | null>(null);
  const [seasons, setSeasons] = useState<Array<{ id: string; label: string; startAt: string; endAt: string }>>([]);
  const [rankingsSeasonId, setRankingsSeasonId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [tradeScope, setTradeScope] = useState<'today' | 'season'>('season');
  const [rankingsSearch, setRankingsSearch] = useState('');
  // Sort + filter for positions table
  type PosSortField = 'symbol' | 'shares' | 'avgCost' | 'bookValue' | 'currentPrice' | 'unrealizedPl';
  const [posSortField, setPosSortField] = useState<PosSortField>('unrealizedPl');
  const [posSortDir, setPosSortDir] = useState<'asc' | 'desc'>('desc');
  const [posSymFilter, setPosSymFilter] = useState('');
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  // Stop editing state
  const [editingStopId, setEditingStopId]     = useState<string | null>(null);
  const [editingStopVal, setEditingStopVal]   = useState('');
  const [savingStopId, setSavingStopId]       = useState<string | null>(null);
  const [recentTrades, setRecentTrades] = useState<Array<{
    id: string;
    symbol: string;
    side: string;
    shares: number;
    price: number;
    totalValue: number;
    timestamp: string;
    realizedPl?: number | null;
    realizedPlPct?: number | null;
    username?: string;
    avatar?: string | null;
  }>>([]);

  const fetchAll = useCallback(async () => {
    setError(null);
    try {
      const [accRes, posRes, snapRes] = await Promise.all([
        fetch('/api/game/account'),
        fetch('/api/game/positions'),
        fetch('/api/game/equity-snapshots'),
      ]);
      if (!accRes.ok) throw new Error('Failed to load account');
      if (!posRes.ok) throw new Error('Failed to load positions');
      const acc = await accRes.json();
      const pos = await posRes.json();
      setAccount(acc);
      setLastUpdated(new Date());
      setPositions(pos.positions ?? []);
      if (snapRes.ok) {
        const snap = await snapRes.json();
        setSnapshots(snap.snapshots ?? []);
      }
      const feedRes = await fetch('/api/game/feed?mine=1&limit=15');
      if (feedRes.ok) {
        const feedData = await feedRes.json();
        setRecentTrades(feedData.feed ?? []);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Something went wrong');
    } finally {
      setLoading(false);
    }
  }, []);

  /** Save a manually-set stop price for a position (updates Ra's stop). */
  const saveStop = useCallback(async (posId: string, rawVal: string) => {
    const trimmed = rawVal.trim().replace(/^\$/, '');
    const isClearing = trimmed === '' || trimmed === '—';
    const stopPrice = isClearing ? null : parseFloat(trimmed);
    if (!isClearing && (!Number.isFinite(stopPrice) || stopPrice! <= 0)) {
      toast.error('Enter a valid price (e.g. 8.50) or leave blank to clear');
      return;
    }
    setSavingStopId(posId);
    try {
      const res = await fetch(`/api/game/positions/${posId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stopPrice }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        toast.error(data.error ?? 'Failed to update stop');
        return;
      }
      // Optimistically update local positions state
      setPositions((prev) => prev.map((p) =>
        p.id === posId
          ? { ...p, stopPrice: data.stopPrice ?? null, manualStop: data.manualStop ?? false }
          : p
      ));
      toast.success(isClearing ? 'Stop cleared, Ra will compute it' : `Stop set to $${stopPrice!.toFixed(2)}`);
    } catch {
      toast.error('Failed to update stop');
    } finally {
      setSavingStopId(null);
      setEditingStopId(null);
    }
  }, []);

  /** Called after a game trade: optimistically add the new trade so it stays visible, then refetch. */
  const onTradeSuccess = useCallback((data?: { trade?: { id: string; symbol: string; side: string; shares: number; price: number; totalValue: number; timestamp: string } }) => {
    if (data?.trade) {
      setRecentTrades((prev) => [
        { ...data.trade!, username: session?.user?.name ?? 'You', avatar: session?.user?.image ?? null },
        ...prev.filter((t) => t.id !== data.trade!.id),
      ].slice(0, 15));
    }
    fetchAll();
  }, [fetchAll]);

  useEffect(() => {
    if (status === 'unauthenticated') return;
    if (status === 'authenticated') {
      fetchAll();
      try {
        sessionStorage.removeItem('game-trade-dirty');
      } catch (_) {}
    }
  }, [status, fetchAll]);

  // Refetch when returning to this tab after a trade from another page (alerts/chart/watchlist)
  useEffect(() => {
    if (status !== 'authenticated') return;
    const onVisibility = () => {
      try {
        if (document.visibilityState === 'visible' && sessionStorage.getItem('game-trade-dirty')) {
          sessionStorage.removeItem('game-trade-dirty');
          fetchAll();
        }
      } catch (_) {}
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [status, fetchAll]);

  // Auto-refresh positions + unrealized P/L every 30s
  useEffect(() => {
    if (status !== 'authenticated') return;
    const interval = setInterval(() => { fetchAll(); }, 30000);
    return () => clearInterval(interval);
  }, [status, fetchAll]);

  const fetchRankings = useCallback(async () => {
    setRankingsLoading(true);
    setRankingsError(null);
    try {
      const url = rankingsSeasonId
        ? `/api/game/rankings?metric=${rankingsMetric}&seasonId=${rankingsSeasonId}`
        : `/api/game/rankings?metric=${rankingsMetric}`;
      const res = await fetch(url);
      if (res.ok) {
        const data = await res.json();
        setRankings(data.rankings ?? []);
      } else {
        setRankings([]);
        setRankingsError('Failed to load rankings');
      }
    } catch {
      setRankings([]);
      setRankingsError('Failed to load rankings');
    } finally {
      setRankingsLoading(false);
    }
  }, [rankingsMetric, rankingsSeasonId]);

  useEffect(() => {
    if (account && status === 'authenticated') fetchRankings();
  }, [account, status, fetchRankings]);

  useEffect(() => {
    if (status !== 'authenticated') return;
    fetch('/api/game/seasons')
      .then((r) => r.ok ? r.json() : { seasons: [] })
      .then((data) => setSeasons(data.seasons ?? []))
      .catch(() => setSeasons([]));
  }, [status]);

  const openBuyNew = () => {
    const el = document.getElementById('new-symbol') as HTMLInputElement | null;
    const sym = el?.value?.trim().toUpperCase();
    if (sym) setBuyNewSymbol(sym); else toast.error('Enter a symbol');
  };

  if (status === 'loading' || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-zinc-400" />
      </div>
    );
  }

  if (status === 'unauthenticated') {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-6 p-6">
        <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-500/15 to-amber-600/5 border border-amber-500/30">
          <Gamepad2 className="h-20 w-20 text-amber-400" />
        </div>
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-white tracking-tight mb-2">Stock Trading Game</h1>
          <p className="text-zinc-400 leading-relaxed">
            Sign in to play the monthly paper trading game. Start with $1,000 and trade with live prices.
          </p>
        </div>
        <Link prefetch={false}
          href="/auth"
          className="px-6 py-3 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-zinc-900 font-semibold shadow-lg shadow-amber-500/20 transition-all"
        >
          Sign in to Play
        </Link>
      </div>
    );
  }

  if (error || !account) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-4 p-4">
        <p className="text-red-400 text-center">{error || 'Failed to load game'}</p>
        <div className="flex flex-wrap gap-2 justify-center">
          <button
            type="button"
            onClick={() => { setLoading(true); fetchAll(); }}
            className="px-4 py-2 rounded-lg bg-amber-500 hover:bg-amber-600 text-zinc-900 font-medium"
          >
            Retry
          </button>
          <Link prefetch={false}
            href="/alerts"
            className="px-4 py-2 rounded-lg border border-zinc-600 text-zinc-300 hover:bg-zinc-800"
          >
            Back to Alerts
          </Link>
        </div>
      </div>
    );
  }

  // Today scope: filter snapshots to today's date (ET local date)
  const todayStr = new Date().toLocaleDateString('en-CA');
  const todaySnapshots = snapshots.filter((s) =>
    new Date(s.timestamp).toLocaleDateString('en-CA') === todayStr
  );
  const activeSnapshots = tradeScope === 'today' ? todaySnapshots : snapshots;
  const todayFirstValue = todaySnapshots.length > 0
    ? todaySnapshots.reduce((min, s) => Math.min(min, s.totalValue), Infinity)
    : account.totalValue;
  const todayPl = account.totalValue - (isFinite(todayFirstValue) ? todayFirstValue : account.totalValue);

  const rawChartData = activeSnapshots.length
    ? activeSnapshots.map((s) => ({ t: s.timestamp, v: s.totalValue }))
    : [{ t: new Date().toISOString(), v: account.totalValue }];
  // Ensure at least 2 points for a line: prepend season-start ($1000) if single point
  const chartData = rawChartData.length === 1
    ? [
        { t: new Date(new Date(rawChartData[0].t).getTime() - 86400000).toISOString(), v: 1000 },
        ...rawChartData,
      ]
    : rawChartData;
  const sortedData = [...chartData].sort((a, b) => new Date(a.t).getTime() - new Date(b.t).getTime());
  // Frame to the ACTUAL data range — do NOT force the $1,000 start into the domain (that squashed
  // real movement whenever the value sat in a band away from $1,000, making the line look flat).
  // The $1,000 reference is drawn separately only when it falls in view (baselineInView, below).
  const vals = sortedData.map((d) => d.v);
  const dataMin = Math.min(...vals);
  const dataMax = Math.max(...vals, account.totalValue);
  const rawRange = dataMax - dataMin;
  const padV = rawRange > 1e-6 ? rawRange * 0.15 : Math.max(dataMax * 0.01, 5);
  const minV = dataMin - padV;
  const maxV = dataMax + padV;
  const range = maxV - minV || 1;
  const baselineInView = 1000 >= minV && 1000 <= maxV;
  const chartW = 600;
  const chartH = 130;
  const pad = { top: 8, right: 12, bottom: 22, left: 8 };
  const innerW = chartW - pad.left - pad.right;
  const innerH = chartH - pad.top - pad.bottom;
  const times = sortedData.map((d) => new Date(d.t).getTime());
  const minT = Math.min(...times);
  const maxT = Math.max(...times);
  const timeRange = maxT - minT || 1;
  const toX = (t: number) => pad.left + ((t - minT) / timeRange) * innerW;
  const toY = (v: number) => pad.top + innerH - ((v - minV) / range) * innerH;
  const pathPoints = sortedData.map((d, i) => {
    const x = toX(new Date(d.t).getTime());
    const y = toY(d.v);
    return `${i === 0 ? 'M' : 'L'} ${x} ${y}`;
  }).join(' ');
  const areaPath = `${pathPoints} L ${toX(maxT)} ${pad.top + innerH} L ${pad.left} ${pad.top + innerH} Z`;

  return (
    <div className="min-h-screen text-zinc-100">
      <div className="max-w-4xl mx-auto p-4 sm:p-6 space-y-8">
        {/* Header */}
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-gradient-to-br from-amber-500/20 to-amber-600/10 border border-amber-500/30 shadow-lg shadow-amber-500/10">
              <Gamepad2 className="h-8 w-8 text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
                Stock Trading Game
              </h1>
              <p className="text-sm text-zinc-400 mt-0.5">
                Season <span className="font-mono text-amber-400/90">{account.label}</span> . Paper trading with live prices
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => { setRefreshing(true); fetchAll().finally(() => setRefreshing(false)); }}
            disabled={refreshing}
            className="p-2.5 rounded-xl text-zinc-400 hover:text-white hover:bg-zinc-800/80 border border-zinc-700/50 hover:border-zinc-600 disabled:opacity-50 transition-all"
            title="Refresh account and positions"
            aria-label="Refresh account and positions"
          >
            <RefreshCw className={`h-5 w-5 ${refreshing ? 'animate-spin' : ''}`} />
          </button>
        </div>
        <p className="text-zinc-500 text-sm flex flex-wrap items-center gap-x-4 gap-y-1 -mt-4">
          Starts at $1,000. Resets monthly. Prices you enter are used for execution.
          <Link href="/game/feed" className="text-amber-400 hover:text-amber-300 font-medium transition-colors">
            {'View trade feed ->'}
          </Link>
        </p>

        {/* Scope Toggle */}
        <div className="flex flex-wrap items-center gap-3 -mt-2">
          {(['today', 'season'] as const).map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => setTradeScope(s)}
              className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-all ${
                tradeScope === s
                  ? 'bg-amber-500 text-zinc-900 shadow-md shadow-amber-500/20'
                  : 'bg-zinc-800 text-zinc-400 hover:text-white border border-zinc-700/50'
              }`}
            >
              {s === 'today' ? 'Today' : 'This Season'}
            </button>
          ))}
          {lastUpdated && (
            <span className="text-xs text-zinc-600">
              Updated {lastUpdated.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit', second: '2-digit' })}
            </span>
          )}
        </div>

        {/* Season Account Summary */}
        <section className="rounded-2xl bg-gradient-to-br from-zinc-900/95 to-zinc-800/90 border border-zinc-700/50 p-5 sm:p-6 shadow-xl shadow-black/20 backdrop-blur-sm">
          <div className="flex flex-col sm:flex-row sm:items-start gap-5 sm:gap-6">
            {/* Primary: account value + season P/L */}
            <div className="flex-1 min-w-0">
              <p className="text-[11px] font-semibold text-zinc-500 uppercase tracking-widest mb-2 flex items-center gap-1.5">
                <BarChart3 className="h-3.5 w-3.5 text-amber-500/70" />
                Season Account · {account.label}
              </p>
              <div className="flex items-baseline gap-3 flex-wrap">
                <span className="text-2xl sm:text-3xl font-bold text-white tracking-tight tabular-nums">
                  {formatMoney(account.totalValue)}
                </span>
                {(() => {
                  const pl = account.totalValue - 1000;
                  const pct = (pl / 1000) * 100;
                  return (
                    <span className={`text-sm font-semibold tabular-nums ${pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                      {pl >= 0 ? '+' : ''}{formatMoney(pl)} ({pct >= 0 ? '+' : ''}{pct.toFixed(2)}%)
                    </span>
                  );
                })()}
              </div>
              <p className="text-[11px] text-zinc-600 mt-1.5">Starting balance: $1,000.00 · Resets monthly</p>
            </div>

            {/* Secondary stats: compact grid */}
            <div className="grid grid-cols-2 gap-x-6 gap-y-3 sm:border-l sm:border-zinc-700/50 sm:pl-6 shrink-0">
              {[
                { label: 'Cash', value: formatMoney(account.cashBalance), color: 'text-zinc-200' },
                { label: 'Positions', value: formatMoney(account.positionsValue), color: 'text-amber-400' },
                {
                  label: tradeScope === 'today' ? "Today's P/L" : 'Realized P/L',
                  value: `${(tradeScope === 'today' ? todayPl : (account.realizedPl ?? 0)) >= 0 ? '+' : ''}${formatMoney(tradeScope === 'today' ? todayPl : (account.realizedPl ?? 0))}`,
                  color: (tradeScope === 'today' ? todayPl : (account.realizedPl ?? 0)) >= 0 ? 'text-emerald-400' : 'text-red-400',
                },
                {
                  label: 'Unrealized',
                  value: `${(account.unrealizedPl ?? 0) >= 0 ? '+' : ''}${formatMoney(account.unrealizedPl ?? 0)}`,
                  color: (account.unrealizedPl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400',
                },
              ].map((stat) => (
                <div key={stat.label}>
                  <p className="text-[10px] text-zinc-600 uppercase tracking-wider font-medium mb-0.5">{stat.label}</p>
                  <p className={`text-sm font-bold tabular-nums ${stat.color}`}>{stat.value}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Equity chart */}
        {(sortedData.length > 0 || tradeScope === 'today') && (
          <section className="rounded-2xl bg-gradient-to-br from-zinc-900/95 to-zinc-800/90 border border-zinc-700/50 px-5 pt-4 pb-3 sm:px-6 shadow-xl shadow-black/20 backdrop-blur-sm">
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-xs font-semibold text-zinc-500 uppercase tracking-widest flex items-center gap-2">
                <DollarSign className="h-3.5 w-3.5 text-amber-500/70" />
                {tradeScope === 'today' ? "Today's equity" : 'Season equity'}
              </h2>
              {(() => {
                const pl = account.totalValue - 1000;
                const pct = (pl / 1000) * 100;
                return (
                  <span className={`text-xs font-semibold tabular-nums ${pl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                    {pl >= 0 ? '+' : ''}{pct.toFixed(2)}% vs start
                  </span>
                );
              })()}
            </div>
            <div className="w-full">
              <svg
                viewBox={`0 0 ${chartW} ${chartH}`}
                preserveAspectRatio="none"
                className="w-full h-28"
                role="img"
                aria-label="Account value line chart"
              >
                <defs>
                  <linearGradient id="gameEquityGradient" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="rgba(245, 158, 11, 0.25)" />
                    <stop offset="100%" stopColor="rgba(245, 158, 11, 0)" />
                  </linearGradient>
                </defs>
                {/* $1,000 baseline reference, only when it falls within the framed range */}
                {baselineInView && (
                  <>
                    <line
                      x1={pad.left}
                      x2={pad.left + innerW}
                      y1={toY(1000)}
                      y2={toY(1000)}
                      stroke="rgba(113,113,122,0.35)"
                      strokeWidth="1"
                      strokeDasharray="5,4"
                    />
                    <text
                      x={pad.left + innerW}
                      y={toY(1000) - 4}
                      textAnchor="end"
                      className="text-[9px] fill-zinc-600"
                      fontSize="9"
                    >
                      start $1,000
                    </text>
                  </>
                )}
                <path d={areaPath} fill="url(#gameEquityGradient)" />
                <path
                  d={pathPoints}
                  fill="none"
                  stroke="rgb(245, 158, 11)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
                {/* Only show dots when there are enough points to warrant them */}
                {sortedData.length > 2 && sortedData.map((d) => (
                  <circle
                    key={d.t}
                    cx={toX(new Date(d.t).getTime())}
                    cy={toY(d.v)}
                    r="2"
                    fill="rgb(245, 158, 11)"
                  >
                    <title>{`${new Date(d.t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}, ${formatMoney(d.v)}`}</title>
                  </circle>
                ))}
                {/* First and last date labels */}
                {sortedData.length >= 2 && <>
                  <text x={pad.left} y={chartH - 4} textAnchor="start" fontSize="9" className="fill-zinc-600">
                    {new Date(sortedData[0]!.t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </text>
                  <text x={pad.left + innerW} y={chartH - 4} textAnchor="end" fontSize="9" className="fill-zinc-600">
                    {new Date(sortedData[sortedData.length - 1]!.t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })}
                  </text>
                </>}
              </svg>
            </div>
          </section>
        )}

        {/* Rankings */}
        <section className="rounded-2xl bg-gradient-to-br from-zinc-900/95 to-zinc-800/90 border border-zinc-700/50 p-5 sm:p-6 shadow-xl shadow-black/20 backdrop-blur-sm">
          {/* Header row */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <h2 className="text-xs font-semibold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
              <Trophy className="h-4 w-4 text-amber-500/80" /> Season Rankings
            </h2>
            <div className="flex items-center gap-2">
              {/* Search */}
              <div className="relative">
                <input
                  type="text"
                  placeholder="Search player…"
                  value={rankingsSearch}
                  onChange={(e) => setRankingsSearch(e.target.value)}
                  className="w-36 sm:w-44 pl-2.5 pr-7 py-1.5 text-xs rounded-lg bg-zinc-800/80 border border-zinc-700/60 text-zinc-200 placeholder-zinc-600 focus:outline-none focus:border-amber-500/50 focus:bg-zinc-800 transition-colors"
                />
                {rankingsSearch && (
                  <button
                    type="button"
                    onClick={() => setRankingsSearch('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300 text-xs leading-none"
                    aria-label="Clear search"
                  >×</button>
                )}
              </div>
              {rankings.length > 0 && (
                <span className="text-xs text-zinc-600 whitespace-nowrap">{rankings.length} player{rankings.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          </div>

          {/* Season selector */}
          {seasons.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5 mb-3">
              <span className="text-[11px] text-zinc-500 font-medium">Season:</span>
              <button
                type="button"
                onClick={() => setRankingsSeasonId(null)}
                className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${rankingsSeasonId === null ? 'bg-amber-500 text-zinc-900 shadow-sm shadow-amber-500/20' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200 border border-zinc-700/50'}`}
              >
                {account.label} (current)
              </button>
              {seasons.filter((s) => s.id !== account.seasonId).slice(0, 6).map((s) => (
                <button
                  key={s.id}
                  type="button"
                  onClick={() => setRankingsSeasonId(s.id)}
                  className={`px-2.5 py-1 rounded-lg text-[11px] font-semibold transition-all ${rankingsSeasonId === s.id ? 'bg-amber-500 text-zinc-900 shadow-sm shadow-amber-500/20' : 'bg-zinc-800 text-zinc-400 hover:text-zinc-200 border border-zinc-700/50'}`}
                >
                  {s.label}
                </button>
              ))}
              {rankingsSeasonId && (
                <Link
                  href={`/game/feed?seasonId=${encodeURIComponent(rankingsSeasonId)}`}
                  className="text-[11px] text-amber-400/80 hover:text-amber-300 ml-1 transition-colors"
                >
                  Trade feed →
                </Link>
              )}
            </div>
          )}

          {/* Metric tabs */}
          <div className="flex flex-wrap gap-1.5 mb-5 p-1 rounded-xl bg-zinc-800/60 border border-zinc-700/40 w-fit max-w-full">
            {[
              { id: 'account_value' as const, label: 'Account Value', Icon: DollarSign },
              { id: 'biggest_winner' as const, label: 'Top Gains', Icon: TrendingUp },
              { id: 'biggest_loser' as const, label: 'Top Losses', Icon: TrendingDown },
            ].map(({ id, label, Icon }) => (
              <button
                key={id}
                type="button"
                onClick={() => setRankingsMetric(id)}
                className={`px-3 py-1.5 rounded-lg text-xs font-semibold flex items-center gap-1.5 transition-all ${
                  rankingsMetric === id
                    ? 'bg-amber-500 text-zinc-900 shadow-md shadow-amber-500/25'
                    : 'text-zinc-400 hover:text-zinc-200'
                }`}
              >
                <Icon className="h-3.5 w-3.5" /> {label}
              </button>
            ))}
          </div>

          {rankingsLoading ? (
            <div className="flex justify-center py-10">
              <Loader2 className="h-6 w-6 animate-spin text-zinc-500" />
            </div>
          ) : rankings.length === 0 ? (
            <div className="py-6 text-center">
              {rankingsError ? (
                <>
                  <p className="text-red-400/90 text-sm mb-2">{rankingsError}</p>
                  <button type="button" onClick={() => fetchRankings()} className="px-3 py-1.5 rounded-lg bg-amber-500 hover:bg-amber-600 text-zinc-900 text-sm font-medium">Retry</button>
                </>
              ) : (
                <p className="text-zinc-500 text-sm">No rankings yet for this season.</p>
              )}
            </div>
          ) : (() => {
            const myUserId = (session?.user as { id?: string })?.id;
            const BOT_NAMES = new Set(['Ra', 'Thoth', 'Osiris']);
            const BOT_STYLE: Record<string, { text: string; badge: string; ring: string }> = {
              Ra:     { text: 'text-violet-400', badge: 'bg-violet-500/15 text-violet-300 ring-1 ring-violet-500/30', ring: 'ring-violet-500/40' },
              Thoth:  { text: 'text-amber-400',  badge: 'bg-amber-500/15 text-amber-300 ring-1 ring-amber-500/30',   ring: 'ring-amber-500/50'  },
              Osiris: { text: 'text-sky-400',    badge: 'bg-sky-500/15 text-sky-300 ring-1 ring-sky-500/30',         ring: 'ring-sky-500/40'    },
            };
            const RANK_MEDAL = ['🥇', '🥈', '🥉'];
            const DISPLAY_LIMIT = 25;

            const filtered = rankingsSearch
              ? rankings.filter((r) => r.username.toLowerCase().includes(rankingsSearch.toLowerCase()))
              : rankings;

            const myRank = myUserId ? rankings.find((r) => r.userId === myUserId) : null;
            const myRankInFiltered = myRank ? filtered.findIndex((r) => r.userId === myUserId) : -1;
            const myRankVisible = myRankInFiltered >= 0 && myRankInFiltered < DISPLAY_LIMIT;
            const myRankOutsideTop = myRank && !myRankVisible;

            return (
              <div>
                {/* "Your rank" pin, shown only when the current user is outside the visible range */}
                {myRankOutsideTop && rankingsMetric === 'account_value' && (
                  <div className="mb-3 px-3 py-2.5 rounded-xl bg-amber-500/8 border border-amber-500/25 flex items-center gap-3">
                    <div className="h-7 w-7 rounded-full bg-amber-500/20 ring-2 ring-amber-500/40 flex items-center justify-center text-xs font-bold text-amber-400 shrink-0">
                      #{myRank!.rank}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-amber-300">Your ranking</p>
                      <p className="text-[11px] text-zinc-500">Rank #{myRank!.rank} of {rankings.length} · {formatMoney(myRank!.value)}</p>
                    </div>
                    <span className="text-[11px] font-semibold text-amber-400/70">#{myRank!.rank}</span>
                  </div>
                )}

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="text-[11px] font-semibold text-zinc-500 uppercase tracking-wider border-b border-zinc-700/60">
                        <th className="pb-2.5 pr-3 w-8">#</th>
                        <th className="pb-2.5 pr-4">Player</th>
                        {(rankingsMetric === 'biggest_winner' || rankingsMetric === 'biggest_loser') && (
                          <th className="pb-2.5 pr-4">Symbol</th>
                        )}
                        <th className="pb-2.5 pr-4 text-right">
                          {rankingsMetric === 'account_value' ? 'Portfolio Value' : rankingsMetric === 'biggest_winner' ? 'Gain %' : 'Loss %'}
                        </th>
                        {rankingsMetric === 'account_value' && (
                          <>
                            <th className="pb-2.5 pr-4 text-right">Realized P/L</th>
                            <th className="pb-2.5 text-right hidden sm:table-cell">Unrealized</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-zinc-800/60">
                      {filtered.slice(0, DISPLAY_LIMIT).map((r) => {
                        const isMe = myUserId === r.userId;
                        const isBot = BOT_NAMES.has(r.username);
                        const botStyle = isBot ? BOT_STYLE[r.username] : null;
                        const isPodium = r.rank <= 3;
                        return (
                          <tr
                            key={rankingsMetric === 'account_value' ? r.userId : `${r.rank}-${r.userId}-${r.symbol ?? ''}`}
                            className={`transition-colors ${
                              isMe
                                ? 'bg-amber-500/8 hover:bg-amber-500/12'
                                : 'hover:bg-zinc-800/30'
                            }`}
                          >
                            {/* Rank */}
                            <td className="py-3 pr-3 w-10">
                              {r.rank <= 3 ? (
                                <span className="text-base leading-none" title={`Rank ${r.rank}`}>{RANK_MEDAL[r.rank - 1]}</span>
                              ) : (
                                <span className={`text-xs font-mono tabular-nums ${isMe ? 'text-amber-400 font-bold' : 'text-zinc-500'}`}>{r.rank}</span>
                              )}
                            </td>

                            {/* Player */}
                            <td className="py-3 pr-4">
                              <Link href={`/profile/${r.username}`} className="flex items-center gap-2.5 group">
                                <div className="relative shrink-0">
                                  {r.avatar ? (
                                    <Image
                                      src={r.avatar}
                                      alt=""
                                      width={32}
                                      height={32}
                                      unoptimized
                                      className={`h-8 w-8 rounded-full object-cover ring-2 transition-all group-hover:ring-amber-500/60 ${
                                        isMe ? 'ring-amber-500/50' : isPodium ? 'ring-amber-500/30' : botStyle ? botStyle.ring : 'ring-zinc-700/40'
                                      }`}
                                    />
                                  ) : (
                                    <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-bold ring-2 transition-all group-hover:ring-amber-500/60 ${
                                      isMe ? 'bg-amber-500/20 text-amber-400 ring-amber-500/50' :
                                      botStyle ? `bg-zinc-800 ${botStyle.text} ${botStyle.ring}` :
                                      isPodium ? 'bg-amber-500/15 text-amber-400 ring-amber-500/30' :
                                      'bg-zinc-800 text-zinc-400 ring-zinc-700/40'
                                    }`}>
                                      {r.username.slice(0, 1).toUpperCase()}
                                    </div>
                                  )}
                                  {isMe && (
                                    <span className="absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full bg-amber-500 ring-2 ring-zinc-900 flex items-center justify-center" />
                                  )}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5 flex-wrap">
                                    <span className={`text-sm font-semibold leading-tight truncate ${
                                      isMe ? 'text-amber-300' : botStyle ? botStyle.text : isPodium ? 'text-zinc-100' : 'text-zinc-300 group-hover:text-zinc-100'
                                    }`}>
                                      {r.username}
                                    </span>
                                    {isMe && (
                                      <span className="text-[10px] bg-amber-500/20 text-amber-400 px-1.5 py-0.5 rounded-full font-semibold leading-none ring-1 ring-amber-500/30">You</span>
                                    )}
                                    {isBot && (
                                      <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-semibold leading-none ${botStyle!.badge}`}>AI</span>
                                    )}
                                  </div>
                                </div>
                              </Link>
                            </td>

                            {/* Symbol (winner/loser) */}
                            {(rankingsMetric === 'biggest_winner' || rankingsMetric === 'biggest_loser') && (
                              <td className="py-3 pr-4">
                                <Link href={`/chart/${r.symbol}`} target="_blank" rel="noopener noreferrer" className="font-mono text-[13px] text-amber-400 hover:text-amber-300 hover:underline font-bold transition-colors">
                                  {r.symbol}
                                </Link>
                              </td>
                            )}

                            {/* Value */}
                            <td className="py-3 pr-4 text-right">
                              {rankingsMetric === 'account_value' ? (
                                <div>
                                  <span className={`text-sm font-bold tabular-nums ${isMe ? 'text-amber-300' : 'text-zinc-100'}`}>
                                    {formatMoney(r.value)}
                                  </span>
                                  <div className={`text-[11px] font-semibold tabular-nums ${(r.value - 1000) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {(r.value - 1000) >= 0 ? '+' : ''}{(((r.value - 1000) / 1000) * 100).toFixed(2)}%
                                  </div>
                                </div>
                              ) : (
                                <span className={`text-sm font-bold tabular-nums ${r.value >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                  {r.value >= 0 ? '+' : ''}{r.value.toFixed(2)}%{r.isUnrealized ? ' *' : ''}
                                </span>
                              )}
                            </td>

                            {/* Realized / Unrealized (account_value only) */}
                            {rankingsMetric === 'account_value' && (
                              <>
                                <td className="py-3 pr-4 text-right">
                                  <span className={`text-xs font-semibold tabular-nums ${(r.realizedPl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {(r.realizedPl ?? 0) >= 0 ? '+' : ''}{formatMoney(r.realizedPl ?? 0)}
                                  </span>
                                </td>
                                <td className="py-3 text-right hidden sm:table-cell">
                                  <span className={`text-xs font-semibold tabular-nums ${(r.unrealizedPl ?? 0) >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                                    {(r.unrealizedPl ?? 0) >= 0 ? '+' : ''}{formatMoney(r.unrealizedPl ?? 0)}
                                  </span>
                                </td>
                              </>
                            )}
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Footer */}
                <div className="mt-3 pt-3 border-t border-zinc-800/60 flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    {(rankingsMetric === 'biggest_winner' || rankingsMetric === 'biggest_loser') && (
                      <span className="text-[11px] text-zinc-600">* = unrealized (open position)</span>
                    )}
                    {filtered.length > DISPLAY_LIMIT && !rankingsSearch && (
                      <span className="text-[11px] text-zinc-600">{filtered.length - DISPLAY_LIMIT} more players</span>
                    )}
                    {rankingsSearch && (
                      <span className="text-[11px] text-zinc-500">{filtered.length} result{filtered.length !== 1 ? 's' : ''} for "{rankingsSearch}"</span>
                    )}
                  </div>
                  {!rankingsSearch && filtered.length > DISPLAY_LIMIT && (
                    <Link
                      href={`/game/rankings?metric=${rankingsMetric}${rankingsSeasonId ? `&seasonId=${rankingsSeasonId}` : ''}`}
                      className="text-xs text-amber-400 hover:text-amber-300 font-semibold transition-colors"
                    >
                      Full leaderboard →
                    </Link>
                  )}
                </div>
              </div>
            );
          })()}
        </section>

        {/* Positions */}
        <section className="rounded-xl bg-zinc-900/80 border border-zinc-700/50 p-4 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
            <h2 className="text-sm font-medium text-zinc-400">Positions</h2>
            <div className="flex items-center gap-2">
              <input
                type="text"
                id="new-symbol"
                aria-label="Symbol to buy (e.g. AAPL)"
                placeholder="Symbol (e.g. AAPL)"
                className="w-28 rounded-lg bg-zinc-800 border border-zinc-600 px-2 py-1.5 text-sm text-white placeholder-zinc-500 focus:outline-none focus:ring-1 focus:ring-amber-500"
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    const el = document.getElementById('new-symbol') as HTMLInputElement;
                    const sym = el?.value?.trim().toUpperCase();
                    if (sym) setBuyNewSymbol(sym);
                  }
                }}
              />
              <button
                type="button"
                onClick={openBuyNew}
                className="px-3 py-1.5 rounded-lg bg-emerald-600/80 hover:bg-emerald-600 text-white text-sm flex items-center gap-1"
              >
                <Plus className="h-4 w-4" /> Buy new
              </button>
            </div>
          </div>
          {positions.length === 0 ? (
            <p className="text-zinc-500 py-6 text-center">
              No positions yet. Use Buy on a symbol from the alerts or chart to add one here, or add a row below for testing.
            </p>
          ) : (() => {
            // Sort + filter helpers (inline IIFE keeps JSX clean)
            const togglePosSort = (field: PosSortField) => {
              if (posSortField === field) setPosSortDir(d => d === 'asc' ? 'desc' : 'asc');
              else { setPosSortField(field); setPosSortDir('desc'); }
            };
            const PosSortIcon = ({ field }: { field: PosSortField }) =>
              posSortField !== field
                ? <span className="text-zinc-700 ml-0.5">↕</span>
                : <span className="text-amber-400 ml-0.5">{posSortDir === 'asc' ? '↑' : '↓'}</span>;
            const sortedPos = [...positions]
              .filter(p => !posSymFilter || p.symbol.toUpperCase().includes(posSymFilter.toUpperCase()))
              .sort((a, b) => {
                const av = a[posSortField] ?? 0;
                const bv = b[posSortField] ?? 0;
                const cmp = av < bv ? -1 : av > bv ? 1 : 0;
                return posSortDir === 'asc' ? cmp : -cmp;
              });
            return (
            <div className="overflow-x-auto">
              {/* Filter bar */}
              <div className="px-1 py-2 flex items-center gap-3 border-b border-zinc-800/60">
                <input
                  type="text"
                  placeholder="Filter symbol…"
                  value={posSymFilter}
                  onChange={e => setPosSymFilter(e.target.value)}
                  className="w-32 px-2.5 py-1 text-xs bg-zinc-800 border border-zinc-700 rounded-md text-white placeholder-zinc-600 focus:outline-none focus:border-amber-500/60"
                />
                {posSymFilter && (
                  <button onClick={() => setPosSymFilter('')} className="text-xs text-zinc-500 hover:text-zinc-300">Clear ×</button>
                )}
                <span className="text-xs text-zinc-600 ml-auto">{sortedPos.length} position{sortedPos.length !== 1 ? 's' : ''}</span>
              </div>
              <table className="w-full text-left text-sm">
            <thead>
                <tr className="border-b border-zinc-700 text-zinc-400 select-none">
                    {([
                      { label: 'Symbol',        field: 'symbol'       as PosSortField },
                      { label: 'Shares',        field: 'shares'       as PosSortField },
                      { label: 'Avg Cost',      field: 'avgCost'      as PosSortField },
                      { label: 'Book Value',    field: 'bookValue'    as PosSortField },
                      { label: 'Current',       field: 'currentPrice' as PosSortField },
                      { label: 'Unrealized P/L',field: 'unrealizedPl' as PosSortField },
                    ] as const).map(col => (
                      <th key={col.field}
                        className="py-2 pr-4 cursor-pointer hover:text-zinc-200 transition-colors"
                        onClick={() => togglePosSort(col.field)}>
                        {col.label}<PosSortIcon field={col.field} />
                      </th>
                    ))}
                    <th className="py-2 pr-4 text-zinc-400">Stop</th>
                    <th className="py-2 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedPos.map((p) => (
                    <tr key={p.id} className="border-b border-zinc-800">
                      <td className="py-3 pr-4">
                        <Link href={`/chart/${p.symbol}`} target="_blank" rel="noopener noreferrer" className="font-mono text-amber-400 hover:underline">
                          {p.symbol}
                        </Link>
                      </td>
                      <td className="py-3 pr-4 font-mono">{formatShares(p.shares)}</td>
                      <td className="py-3 pr-4">{formatMoney(p.avgCost)}</td>
                      <td className="py-3 pr-4">{formatMoney(p.bookValue)}</td>
                      <td className="py-3 pr-4">{p.currentPrice != null ? formatMoney(p.currentPrice) : '-'}</td>
                      <td className="py-3 pr-4">
                        {p.unrealizedPl != null ? (
                          <span className={p.unrealizedPl >= 0 ? 'text-emerald-400' : 'text-red-400'}>
                            {p.unrealizedPl >= 0 ? '+' : ''}{formatMoney(p.unrealizedPl)}
                            {p.unrealizedPlPct != null ? ` (${p.unrealizedPlPct >= 0 ? '+' : ''}${p.unrealizedPlPct.toFixed(2)}%)` : ''}
                          </span>
                        ) : '-'}
                      </td>
                      {/* ── Stop column ── */}
                      <td className="py-3 pr-4">
                        {editingStopId === p.id ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              autoFocus
                              value={editingStopVal}
                              onChange={(e) => setEditingStopVal(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') void saveStop(p.id, editingStopVal);
                                if (e.key === 'Escape') setEditingStopId(null);
                              }}
                              placeholder="e.g. 8.50"
                              className="w-20 bg-zinc-800 border border-zinc-600 text-white text-xs font-mono rounded px-2 py-1 focus:outline-none focus:border-amber-500"
                            />
                            <button
                              type="button"
                              onClick={() => void saveStop(p.id, editingStopVal)}
                              disabled={savingStopId === p.id}
                              className="text-emerald-400 hover:text-emerald-300 text-xs px-1 disabled:opacity-40"
                              title="Save"
                            >✓</button>
                            <button
                              type="button"
                              onClick={() => setEditingStopId(null)}
                              className="text-zinc-500 hover:text-zinc-300 text-xs px-1"
                              title="Cancel"
                            >✗</button>
                          </div>
                        ) : (
                          <button
                            type="button"
                            onClick={() => {
                              setEditingStopId(p.id);
                              setEditingStopVal(p.stopPrice != null ? p.stopPrice.toFixed(2) : '');
                            }}
                            className="group flex items-center gap-1.5 text-left"
                            title={p.stopPrice != null ? 'Click to edit stop' : 'No stop set, click to add one'}
                          >
                            {p.stopPrice != null ? (
                              <>
                                <span className={`font-mono text-xs tabular-nums ${p.manualStop ? 'text-amber-400' : 'text-red-400'}`}>
                                  ${p.stopPrice.toFixed(2)}
                                </span>
                                {p.currentPrice != null && (
                                  <span className="text-zinc-600 text-[10px] tabular-nums">
                                    ({(((p.stopPrice - p.currentPrice) / p.currentPrice) * 100).toFixed(1)}%)
                                  </span>
                                )}
                                {p.manualStop && (
                                  <span className="text-[9px] text-amber-500/70 font-medium">M</span>
                                )}
                              </>
                            ) : (
                              <span className="text-zinc-700 text-xs group-hover:text-zinc-500 transition-colors">—</span>
                            )}
                          </button>
                        )}
                      </td>
                      <td className="py-3 text-right">
                        <GameBuySell
                          symbol={p.symbol}
                          defaultPrice={p.avgCost}
                          compact
                          onSuccess={onTradeSuccess}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )})()}
          <p className="text-zinc-500 text-xs mt-3">
            To open a new position, go to <Link prefetch={false} href="/alerts" className="text-amber-400/80 hover:underline">Live Alerts</Link> or{' '}
            <Link href="/chart/AAPL" target="_blank" rel="noopener noreferrer" className="text-amber-400/80 hover:underline">Chart</Link> and use Buy/Sell there (when available).
          </p>
        </section>

        {/* Recent Trades */}
        <section className="rounded-2xl bg-zinc-900/80 border border-zinc-700/40 p-5 sm:p-6 shadow-xl shadow-black/30">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xs font-semibold text-zinc-400 uppercase tracking-widest flex items-center gap-2">
              <RefreshCw className="h-3.5 w-3.5 text-amber-500/80" /> Recent Trades
            </h2>
            <Link href="/game/feed" className="text-xs text-amber-400/80 hover:text-amber-300 transition-colors">
              View all →
            </Link>
          </div>

          {recentTrades.length === 0 ? (
            <p className="text-zinc-500 py-6 text-center text-sm">
              No trades yet. Buy or sell a position to get started.
            </p>
          ) : (
            <ul className="space-y-1">
              {recentTrades.map((t) => {
                const isThoth   = t.username === 'Thoth';
                const isCloud   = t.username === 'Cloud';
                const isRagnar  = t.username === 'Ragnar';
                const isRa      = t.username === 'Ra';
                const isOsiris  = t.username === 'Osiris';
                const isSolomon = t.username === 'Solomon';
                const isWatcher = t.username === 'Watcher';
                const avatarSrc = isThoth   ? THOTH_AVATAR
                  : isCloud   ? CLOUD_AVATAR
                  : isRagnar  ? RAGNAR_AVATAR
                  : isRa      ? RA_AVATAR
                  : isOsiris  ? OSIRIS_AVATAR
                  : isSolomon ? SOLOMON_AVATAR
                  : isWatcher ? WATCHER_AVATAR
                  : (t.avatar ?? null);
                const initial = (t.username ?? 'U').slice(0, 1).toUpperCase();
                const isBuy = t.side === 'buy';
                return (
                  <li
                    key={t.id}
                    className="flex items-center gap-3 py-3 border-b border-zinc-800/60 last:border-0 group"
                  >
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {avatarSrc ? (
                        <Image
                          src={avatarSrc}
                          alt={t.username ?? ''}
                          width={44}
                          height={44}
                          unoptimized
                          className={`h-11 w-11 rounded-full object-cover ${
                            isThoth  ? 'ring-2 ring-amber-400/70 shadow-[0_0_12px_rgba(251,191,36,0.35)]'
                            : isCloud  ? 'ring-2 ring-sky-400/70 shadow-[0_0_12px_rgba(56,189,248,0.35)]'
                            : isRagnar ? 'ring-2 ring-orange-500/70 shadow-[0_0_12px_rgba(249,115,22,0.35)]'
                            : 'ring-1 ring-zinc-700/60'
                          }`}
                        />
                      ) : (
                        <div className={`h-11 w-11 rounded-full flex items-center justify-center text-sm font-bold ${
                          isThoth  ? 'bg-amber-500/20 text-amber-400 ring-2 ring-amber-400/70 shadow-[0_0_12px_rgba(251,191,36,0.35)]'
                          : isCloud  ? 'bg-sky-500/20 text-sky-400 ring-2 ring-sky-400/70 shadow-[0_0_12px_rgba(56,189,248,0.35)]'
                          : isRagnar ? 'bg-orange-500/20 text-orange-400 ring-2 ring-orange-500/70 shadow-[0_0_12px_rgba(249,115,22,0.35)]'
                          : 'bg-zinc-700/80 text-zinc-300 ring-1 ring-zinc-600/40'
                        }`}>
                          {initial}
                        </div>
                      )}
                    </div>

                    {/* Body */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className={`text-sm font-semibold ${
                          isThoth  ? 'text-amber-400'
                          : isCloud  ? 'text-sky-400'
                          : isRagnar ? 'text-orange-400'
                          : 'text-zinc-100'
                        }`}>
                          {t.username}
                          {isThoth  && <span className="ml-1 text-amber-500/80">⚡</span>}
                          {isCloud  && <span className="ml-1 text-sky-500/80">🗡</span>}
                          {isRagnar && <span className="ml-1 text-orange-500/80">🪓</span>}
                        </span>
                        <span className={`inline-flex items-center px-1.5 py-0.5 rounded-md text-[11px] font-bold tracking-wide ${
                          isBuy ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'
                        }`}>
                          {isBuy ? '▲ BUY' : '▼ SELL'}
                        </span>
                        <Link
                          href={`/chart/${t.symbol}`} target="_blank" rel="noopener noreferrer"
                          className="font-mono text-amber-400 font-bold text-sm hover:text-amber-300 transition-colors"
                        >
                          {t.symbol}
                        </Link>
                      </div>
                      <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                        <span className="text-xs text-zinc-500">
                          {formatShares(t.shares)} sh @ {formatMoney(t.price)}
                        </span>
                        {t.side === 'sell' && t.realizedPl != null && (
                          <span className={`text-xs font-semibold ${t.realizedPl >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                            {t.realizedPl >= 0 ? '+' : ''}{formatMoney(t.realizedPl)}
                            {t.realizedPlPct != null && (
                              <span className="opacity-75 ml-0.5">
                                ({t.realizedPlPct >= 0 ? '+' : ''}{t.realizedPlPct.toFixed(1)}%)
                              </span>
                            )}
                          </span>
                        )}
                        <span className="text-[11px] text-zinc-600">{formatRelTime(t.timestamp)}</span>
                      </div>
                    </div>

                    {/* Total */}
                    <div className="flex-shrink-0 text-right">
                      <span className="text-sm font-mono font-medium text-zinc-300">
                        {formatMoney(t.totalValue)}
                      </span>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      </div>

      {/* Buy new: controlled GameBuySell (modal only, no buttons) */}
      {buyNewSymbol && (
        <GameBuySell
          symbol={buyNewSymbol}
          openForSide="buy"
          onClose={() => {
            setBuyNewSymbol(null);
            const el = document.getElementById('new-symbol') as HTMLInputElement | null;
            if (el) el.value = '';
          }}
          onSuccess={onTradeSuccess}
        />
      )}
    </div>
  );
}
